import React, { useState, useContext, useMemo } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Dashboard from './Dashboard';
import ProductManagement from './ProductManagement/index.js';
import ProductsList from './ProductManagement/ProductsList';
import ProductCategories from './ProductManagement/ProductCategories';
import WarehousesAndStorage from './WarehousesAndStorage';
import SupplierManagement from './SupplierManagement';
import ShippingAndReceiving from './ShippingAndReceiving';
import Operations from './Operations';
import DRAGGITRM from './DraggitRM';
import AllEndpoints from './AllEndpoints';
import { AuthUserContext } from '../Session';
import ErrorBoundary from '../common/ErrorBoundary';
import * as ROUTES from '../../constants/routes';
import './inventory.css';

const Inventory = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const authUser = useContext(AuthUserContext);

  // Get user's organization from roles (similar to TakeOff.js)
  const organization = useMemo(() => {
    if (!authUser?.roles) return null;
    const userOrgs = Object.keys(authUser.roles).filter(key => authUser.roles[key] !== null);
    return userOrgs.length > 0 ? userOrgs[0] : null; // Use first organization
  }, [authUser]);

  // Derive child paths safely (slice instead of fragile .replace)
  const stripPrefix = (route) => route.slice(ROUTES.INVENTORY.length);
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="inventory-layout">
      <aside className={`inventory-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <header className="inventory-sidebar-header">
          <div className="inventory-sidebar-title">
            {!sidebarCollapsed && t('inventory.sidebar_title')}
          </div>
          <button
            onClick={(event) => {
              setSidebarCollapsed(!sidebarCollapsed);
              event.currentTarget.blur(); // Unfocus the button
            }}
            className="inventory-collapse-btn"
            title={sidebarCollapsed ? t('inventory.expand_sidebar') : t('inventory.collapse_sidebar')} // Tooltip text
          >
            {sidebarCollapsed ? '📋' : '⬅️'}
          </button>
        </header>

        <nav className="inventory-nav">
          <Link
            to={`${ROUTES.INVENTORY}/dashboard`}
            className={`inventory-nav-link ${location.pathname === `${ROUTES.INVENTORY}/dashboard` ? 'active' : ''}`}
            title={t('inventory.dashboard_tooltip')} // Tooltip text
          >
            <span className="inventory-nav-icon">📊</span>
            {!sidebarCollapsed && <span>{t('inventory.dashboard')}</span>}
          </Link>

          <section className="inventory-nav-section">
            <Link
              to={ROUTES.INVENTORY_PRODUCT_MANAGEMENT}
              className={`inventory-nav-link ${location.pathname === ROUTES.INVENTORY_PRODUCT_MANAGEMENT ? 'active' : ''}`}
              title={t('inventory.products_management_tooltip')} // Tooltip text
            >
              <span className="inventory-nav-icon">⚙️</span>
              {!sidebarCollapsed && <span>{t('inventory.products_management')}</span>}
            </Link>
          </section>

                    <section className="inventory-nav-section">
            <Link
              to={ROUTES.INVENTORY_OPERATIONS}
              className={`inventory-nav-link ${location.pathname === ROUTES.INVENTORY_OPERATIONS ? 'active' : ''}`}
              title={t('inventory.operations_tooltip')} // Tooltip text
            >
              <span className="inventory-nav-icon">💼</span>
              {!sidebarCollapsed && <span>{t('inventory.operations')}</span>}
            </Link>
          </section>

          <section className="inventory-nav-section">
            <Link
              to={ROUTES.INVENTORY_WAREHOUSES_AND_STORAGE}
              className={`inventory-nav-link ${location.pathname === ROUTES.INVENTORY_WAREHOUSES_AND_STORAGE ? 'active' : ''}`}
              title={t('inventory.warehouses_tooltip')} // Tooltip text
            >
              {!!sidebarCollapsed && '🏭 '}
              {!sidebarCollapsed && t('inventory.warehouses')}
            </Link>
          </section>

          <section className="inventory-nav-section">
            <Link
              to={`${ROUTES.INVENTORY}/suppliers`}
              className={`inventory-nav-link ${location.pathname === `${ROUTES.INVENTORY}/suppliers` ? 'active' : ''}`}
              title={t('inventory.suppliers_tooltip')} // Tooltip text
            >
              {!!sidebarCollapsed && '🤝 '}
              {!sidebarCollapsed && t('inventory.suppliers_list')}
            </Link>
          </section>

          <section className="inventory-nav-section">
            <Link
              to={ROUTES.INVENTORY_SHIPPING_AND_RECEIVING}
              className={`inventory-nav-link ${location.pathname === ROUTES.INVENTORY_SHIPPING_AND_RECEIVING ? 'active' : ''}`}
              title={t('inventory.shipping_receiving_tooltip')} // Tooltip text
            >
              {!!sidebarCollapsed && '🚚 '}
              {!sidebarCollapsed && t('inventory.shipping_receiving')}
            </Link>
            <Link
              to={ROUTES.DRAGGITRM}
              className={`inventory-nav-link ${location.pathname === ROUTES.DRAGGITRM ? 'active' : ''}`}
              title={t('inventory.resource_management_tooltip')} // Tooltip text
            >
              {!!sidebarCollapsed && '🔧 '}
              {!sidebarCollapsed && t('inventory.resource_management')}
            </Link>
          </section>

          {isDev && (
            <section className="inventory-nav-section">
            <Link
              to={ROUTES.INVENTORY_ALL_ENDPOINTS}
              className={`inventory-nav-link ${location.pathname === ROUTES.INVENTORY_ALL_ENDPOINTS ? 'active' : ''}`}
              title={t('inventory.api_endpoints_tooltip')} // Tooltip text
            >
              {!!sidebarCollapsed && '🧪 '}
              {!sidebarCollapsed && t('inventory.api_endpoints')}
            </Link>
          </section>
          )}
        </nav>
      </aside>

      <div className="inventory-main">
        <ErrorBoundary>
          <Routes>
          <Route path="/" element={<Dashboard organization={organization} />} />
          <Route path="/dashboard" element={<Dashboard organization={organization} />} />
          <Route path="/products" element={<ProductsList organization={organization} />} />
          <Route path="/suppliers" element={<SupplierManagement organization={organization} />} />
          <Route
            path={stripPrefix(ROUTES.DRAGGITRM)}
            element={<DRAGGITRM organization={organization} />}
          />
          <Route
            path={stripPrefix(ROUTES.INVENTORY_PRODUCT_MANAGEMENT)}
            element={<ProductManagement organization={organization} />}
          />
          <Route
            path={stripPrefix(ROUTES.INVENTORY_OPERATIONS)}
            element={<Operations organization={organization} />}
          />
          <Route
            path={stripPrefix(ROUTES.INVENTORY_WAREHOUSES_AND_STORAGE)}
            element={<WarehousesAndStorage organization={organization} />}
          />
          <Route
            path={stripPrefix(ROUTES.INVENTORY_SHIPPING_AND_RECEIVING)}
            element={<ShippingAndReceiving organization={organization} />}
          />
          {isDev && (
            <Route
              path={stripPrefix(ROUTES.INVENTORY_ALL_ENDPOINTS)}
              element={<AllEndpoints organization={organization} />}
            />
          )}
        </Routes>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default Inventory;

